import { BrowserModule } from "@angular/platform-browser";
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, ErrorHandler } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthInterceptor } from "./pages/auth/auth-interceptor";
import { EncounterInterceptor } from "./encounter-interceptor";
import { VexModule } from "../@vex/vex.module";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { CustomLayoutModule } from "./custom-layout/custom-layout.module";
import { MaterialModule } from "../material.module";
import { ErrorInterceptor } from "./error-interceptor";
import { ErrorComponent } from "./pages/error/error.component";
import { ToastrModule } from "ngx-toastr";
import { NgxSpinnerModule } from "ngx-spinner";
import { LocationStrategy, HashLocationStrategy } from "@angular/common";
import { ConfirmDialogBoxComponent } from "./pages/confirm-dialog-box/confirm-dialog-box.component";
import { OwlDateTimeModule, OwlNativeDateTimeModule } from "ng-pick-datetime";
import { AngularFireModule } from "@angular/fire";
import { AngularFireMessagingModule } from "@angular/fire/messaging";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { environment } from "src/environments/environment";
import { DigitalServiceFormComponent } from "./pages/auth/digital-service-form/digital-service-form.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FlexLayoutModule } from "@angular/flex-layout";
import { SignaturePadModule } from "angular2-signaturepad";
import { ThankyouComponent } from "./pages/auth/thankyou/thankyou.component";
import { DemoComponent } from "./demo/demo.component";
import { ThankComponent } from "./demo/thank/thank.component";
import { RecaptchaModule } from "ng-recaptcha";
import { BodyPartsComponent } from "./demo/body-parts/body-parts.component";
import { ProfileImageDialogComponent } from "./pages/auth/profile-image-dialog/profile-image-dialog.component";
import { SessionExpiredDialogComponent } from "./pages/auth/session-expired-dialog/session-expired-dialog.component";
import { CheckEmail2Component } from "./pages/auth/check-email-2/check-email-2.component";
import { PhoneFormatterDirective } from "./phone_2";
import { SSNFormatterDirective } from "./ssn_2";
import { FcmService } from "./fcm.service";
import { ApplicantFormDetailsComponent } from "./pages/eval/evaluation/applicant-form-details/applicant-form-details.component";
import { UpdatePhoneComponent } from "./pages/auth/update-phone/update-phone.component";
import { UpdatePhoneThankYouComponent } from './pages/auth/update-phone-thank-you/update-phone-thank-you.component';
import { ConfigService } from "./services/config.service";

class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly RELOAD_KEY = 'chunk_load_reload_attempted';

  handleError(error: any): void {
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    if (chunkFailedMessage.test(error?.message)) {
      const alreadyReloaded = sessionStorage.getItem(this.RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(this.RELOAD_KEY, '1');
        window.location.reload();
      } else {
        // Second failure after reload — real issue, not stale cache
        sessionStorage.removeItem(this.RELOAD_KEY);
        console.error('[ChunkLoadError] Chunk failed to load even after reload:', error);
      }
    } else {
      console.error(error);
    }
  }
}

function loadConfig(configService: ConfigService) {
  return async () => {
    const config = await configService.loadConfig();
    if (!config?.notificationsDisabled && environment.production && "serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.register("firebase-messaging-sw.js");
        console.log("Service Worker registered with scope:", registration.scope);
      } catch (err) {
        console.error("Service Worker registration failed:", err);
      }
    }
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorComponent,
    ConfirmDialogBoxComponent,
    DigitalServiceFormComponent,
    ThankyouComponent,
    DemoComponent,
    ThankComponent,
    BodyPartsComponent,
    ProfileImageDialogComponent,
    SessionExpiredDialogComponent,
    CheckEmail2Component,
    PhoneFormatterDirective,
    SSNFormatterDirective,
    ApplicantFormDetailsComponent,
    UpdatePhoneComponent,
    UpdatePhoneThankYouComponent
  ],
  imports: [
    BrowserModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireMessagingModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    RecaptchaModule,
    HttpClientModule,
    VexModule,
    CustomLayoutModule,
    ToastrModule.forRoot({
      timeOut: 9000, 
      extendedTimeOut: 1000, 
      positionClass: "toast-bottom-left",
      preventDuplicates: true,
      closeButton: true,
      progressBar: false,
      toastClass: "ngx-toastr wide-toast",
    }),
    NgxSpinnerModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    NgxSpinnerModule,
    InfiniteScrollModule,
    MaterialModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    ConfigService,
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfig,
      deps: [ConfigService],
      multi: true
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EncounterInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    FcmService,
  ],
  bootstrap: [AppComponent],
  entryComponents: [ErrorComponent],
})
export class AppModule {}
